import { DeviceChangeOption } from '../../../common/enums.js';
import { OrderItem } from '../../../generated/api/orderItem';
import {
  findBasketItemForId,
  getCampaignAssociation,
  getCommercialProductForGuid,
  getOffer,
  getOnlineModelForBasketItem,
  getSalesProductCommercialProductsForOrderItem,
} from '../../ShoppingBasket/shoppingBasketUtils.js';
import { requireValue } from './placeOrderUtils';
import type { DeviceChangeOrRedeemRequest } from '../../../common/types/device';
import type { BasketItem } from '../../../common/types/shoppingBasket';
import type { CampaignContext } from '../../../generated/api/campaignContext';
import type { ChildOrderItem } from '../../../generated/api/childOrderItem';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { CampaignData, PlaceOrderItemUnit } from '../placeOrderTypes';

const generateOrderItemUniqueId = (existingIds: Set<string>): string => {
  const newId = crypto.randomUUID();
  if (existingIds.has(newId)) {
    return generateOrderItemUniqueId(existingIds);
  }
  existingIds.add(newId);
  return newId;
};

/**
 * Creates a unique key for grouping order items.
 * Items with the same basket item, addons, enrollment program, and enrollment program alias should be grouped together.
 * Device change units get their own group to ensure they become separate OrderItems.
 */
export const createGroupingKey = (
  basketItem: BasketItem,
  addons: ChildOrderItem[],
  enrollmentProgram?: string,
  enrollmentProgramAlias?: string,
  deviceChange?: DeviceChangeOrRedeemRequest
): string => {
  const addonKeys = addons
    .map(addon => {
      const attrStr = addon.addOnAttributes ? JSON.stringify(addon.addOnAttributes) : '';
      return `${addon.addOnCode}::${addon.addOnAssociationCode}::${attrStr}`;
    })
    .sort((a, b) => a.localeCompare(b, 'en'))
    .join('|');

  // Device change units get unique group key to ensure they split into separate OrderItem
  const deviceChangeSuffix = deviceChange?.replacedSubscriptionId ? `::DC::${deviceChange.replacedSubscriptionId}` : '';

  return `${basketItem.guid}::${basketItem.offer.guid}::${basketItem.offer.commercialProductGuid}::${addonKeys}::${enrollmentProgram ?? ''}::${enrollmentProgramAlias ?? ''}${deviceChangeSuffix}`;
};

/**
 * Groups PlaceOrderItemUnits by their unique characteristics and converts them to OrderItems.
 * Items are grouped when they have:
 * - Same basket item (guid, offer, commercial product)
 * - Same addons (code and association)
 * - Same enrollment program and alias (or both undefined)
 * - No device change (device change units get their own group)
 *
 * The quantity of the resulting OrderItem equals the number of units in the group.
 */
/**
 * Get campaign association code for an addon from voucher campaign contexts.
 * Looks up the addon in campaignContexts by addOnCode and returns the first campaign association code.
 */
const getAddonCampaignAssociationCode = (
  addOnCode: string,
  campaignContexts?: CampaignContext[]
): string | undefined => {
  if (!campaignContexts?.length) {
    return undefined;
  }
  const addonContext = campaignContexts.find(ctx => ctx.addOnCode === addOnCode);
  return addonContext?.campaignAssociations?.[0]?.campaignAssociationCode;
};

export const mapOrderItems = (
  onlineModels: OnlineModel[],
  basketItems: BasketItem[],
  placeOrderItemUnits: PlaceOrderItemUnit[],
  campaignData?: CampaignData
): OrderItem[] => {
  // Group units by their unique characteristics
  const groupMap = new Map<
    string,
    {
      basketItem: BasketItem;
      enrollmentProgram?: string;
      enrollmentProgramAlias?: string;
      addons: ChildOrderItem[];
      units: PlaceOrderItemUnit[];
    }
  >();

  for (const unit of placeOrderItemUnits) {
    const basketItem = requireValue(
      findBasketItemForId(basketItems, unit.basketItemId),
      `Basket item not found for id: ${unit.basketItemId}`
    );

    const groupKey = createGroupingKey(
      basketItem,
      unit.addons,
      unit.enrollmentProgram,
      unit.enrollmentProgramAlias,
      unit.deviceChange
    );
    const existingGroup = groupMap.get(groupKey);

    if (existingGroup) {
      existingGroup.units.push(unit);
    } else {
      groupMap.set(groupKey, {
        basketItem,
        enrollmentProgram: unit.enrollmentProgram,
        enrollmentProgramAlias: unit.enrollmentProgramAlias,
        addons: unit.addons,
        units: [unit],
      });
    }
  }

  // Convert grouped units to OrderItems
  const generatedIds = new Set<string>();
  return Array.from(groupMap.values()).map(group => {
    const source =
      group.basketItem.source === 'OMAELISA' ? OrderItem.SourceEnum.OMAELISA : OrderItem.SourceEnum.PUBLIC_PAGE;

    // Extract OrderItemUnit data (remove basket-specific and device change fields)
    const orderItemUnits = group.units.map(unit => {
      const {
        basketItemId: _basketItemId,
        basketItemIndex: _basketItemIndex,
        addons: _addons,
        enrollmentProgram: _enrollmentProgram,
        enrollmentProgramAlias: _enrollmentProgramAlias,
        deviceChange: _deviceChange,
        ...orderItemUnit
      } = unit;
      return orderItemUnit;
    });

    // Get replacedSubscriptionId from the first unit if it has device change
    // (Due to grouping, device change units are in their own group with exactly 1 unit)
    // For REDEEM, the redeem is handled by a separate epp-redeem-terminate API call.
    // Including replacedSubscriptionId on the order would cause SFDC to create a duplicate Device Change CR.
    const isRedeem = group.units[0]?.deviceChange?.deviceChangeOption === DeviceChangeOption.REDEEM;
    const replacedSubscriptionId = isRedeem ? undefined : group.units[0]?.deviceChange?.replacedSubscriptionId;

    // Deduplicate addons by addOnCode and add campaignAssociationCode from voucher contexts
    const deduplicatedAddons = Array.from(new Map(group.addons.map(addon => [addon.addOnCode, addon])).values()).map(
      addon => {
        const addonCampaignCode = getAddonCampaignAssociationCode(
          addon.addOnCode,
          campaignData?.campaignContextsFromVoucher
        );
        return {
          ...addon,
          addOnAssociationCode: addon.addOnAssociationCode,
          ...(addonCampaignCode ? { campaignAssociationCode: addonCampaignCode } : {}),
        };
      }
    );

    // Filter out addons with empty addOnCode or addOnAssociationCode — these cause backend failures.
    // Empty codes can occur when basket data contains stale/corrupted entries or when CloudSense
    // dependency engine resolves addon IDs that have no matching association (falls back to '').
    const uniqueAddons = deduplicatedAddons.filter(
      addon => addon.addOnCode?.length > 0 && addon.addOnAssociationCode?.length > 0
    );

    const onlineModel = getOnlineModelForBasketItem(onlineModels, group.basketItem);
    const offer = getOffer(group.basketItem.offer.guid, onlineModel);
    const { commercialProductCode: resolvedCommercialProductCode, additionalCommercialProductCodes } =
      getSalesProductCommercialProductsForOrderItem(group.basketItem, onlineModel, offer);

    // Resolve campaignAssociationCode: first from voucher/product-level campaignAssociations,
    // then fallback to price.campaign for devices where campaign data lives on EntityPriceCampaign.

    const campaignAssociationCode =
      getCampaignAssociation(
        campaignData?.voucherCode ?? '',
        campaignData?.campaignsFromVoucher ?? [],
        campaignData?.campaignContextsFromVoucher ?? [],
        onlineModel,
        offer,
        resolvedCommercialProductCode
      )?.campaignAssociationCode ??
      getCommercialProductForGuid(resolvedCommercialProductCode, onlineModel)?.price?.campaign?.campaignAssociation
        ?.campaignAssociationCode;

    return {
      orderItemId: generateOrderItemUniqueId(generatedIds),
      onlineModelCode: group.basketItem.guid,
      commercialProductCode: resolvedCommercialProductCode,
      offerCode: group.basketItem.offer.guid,
      quantity: group.units.length,
      source,
      ...(campaignAssociationCode ? { campaignAssociationCode } : {}),
      ...(additionalCommercialProductCodes?.length ? { additionalCommercialProductCodes } : undefined),
      ...(group.enrollmentProgram
        ? {
            enrollmentProgramSelected: true,
            enrollmentProgramAlias: group.enrollmentProgramAlias,
          }
        : {}),
      ...(replacedSubscriptionId ? { replacedSubscriptionId } : {}),
      orderItemUnits,
      childOrderItems: uniqueAddons,
    } satisfies OrderItem;
  });
};
